import { createFileRoute, useNavigate } from '@tanstack/react-router'

import { useCallback, useEffect, useState } from 'react'
import { createClient } from '../../util/supabase/component'
import { type SubmitHandler, useForm } from 'react-hook-form'
import { FormFieldWithErrors } from '../../components/FormFieldWithErrors'
import { Spinner } from '../../components'
import type { User } from '@supabase/supabase-js'

type FormData = {
  password: string
  password2: string
}

const SetPasswordPage = () => {
  const supabase = createClient()
  const navigate = useNavigate()

  const [user, setUser] = useState<User | null>()

  const {
    register,
    handleSubmit,
    setError,
    clearErrors,
    formState: { errors, isValid, isSubmitting },
    watch,
  } = useForm<FormData>({ mode: 'all' })

  const p1 = watch('password')
  const p2 = watch('password2')

  useEffect(() => {
    const parseAuthParams = () => {
      const hash = window.location.hash.slice(1)
      const search = window.location.search.slice(1)

      // Try both hash and search params
      const allParams = new URLSearchParams(hash || search)

      return {
        access_token: allParams.get('access_token'),
        refresh_token: allParams.get('refresh_token'),
        error: allParams.get('error'),
        error_description: allParams.get('error_description'),
      }
    }

    const params = parseAuthParams()

    if (params.access_token && params.refresh_token) {
      supabase.auth
        .setSession({
          access_token: params.access_token,
          refresh_token: params.refresh_token,
        })
        .then(async ({ error }) => {
          if (error) {
            console.error('Session setting error:', error)
            setError('root.login', { message: error.message })
          } else {
            return await supabase.auth.getUser()
          }
        })
        .then((userResponse) => {
          if (!userResponse) {
            console.error('User setting error:')
            setError('root.login', { message: 'Unable to set session' })
            return
          }
          const { data: user } = userResponse
          setUser(user.user)
        })
    } else if (params.error_description) {
      const decodedError = decodeURIComponent(
        params.error_description.replace(/\+/g, ' '),
      )
      setError('root.login', { message: decodedError })
    }

    const { data: authListener } = supabase.auth.onAuthStateChange(
      (event, session) => {
        if (event === 'SIGNED_IN') {
          setUser(session?.user ?? null)
        }
      },
    )

    return () => {
      authListener.subscription.unsubscribe()
    }
  }, [supabase, setError])

  useEffect(() => {
    if (user && errors.root?.login) clearErrors('root.login')
  }, [clearErrors, user, errors.root?.login, p1, p2])

  const onSubmit: SubmitHandler<FormData> = useCallback(
    async (data) => {
      clearErrors()
      const {
        data: { user },
        error,
      } = await supabase.auth.updateUser({
        password: data.password,
      })

      if (error) {
        console.log('error', error)
        setError('root.login', { message: error.message })
        return
      }
      if (user) {
        navigate({ to: '/' })
      }
    },
    [clearErrors, setError, navigate, supabase],
  )

  return (
    <div className="mt-6 flex flex-col pb-6 sm:w-full md:w-1/2 lg:w-1/3 gap-4">
      <div>
        <h4 className="mb-2 text-xl font-bold text-gray-800">Set Password</h4>
        <p>In order to use SID, you must set a password.</p>
      </div>
      {!user && errors.root?.login && (
        <div className="rounded-md border bg-orange-200 p-2 text-xs text-orange-800 flex align-right">
          {errors.root.login.message}
        </div>
      )}
      {user && (
        <form onSubmit={handleSubmit(onSubmit)} className="flex flex-col gap-2">
          <FormFieldWithErrors
            label="Password"
            type="password"
            registerProps={register('password', {
              required: 'Required',
              minLength: { value: 6, message: 'Minimum length of 6' },
            })}
            error={errors.password}
          />
          <FormFieldWithErrors
            label="Confirm Password"
            type="password"
            registerProps={register('password2', {
              required: 'Required',
              minLength: { value: 6, message: 'Minimum length of 6' },
              validate: (value) =>
                value === watch('password') || 'Passwords do not match',
            })}
            error={errors.password2}
          />
          {errors.root?.login && (
            <div className="rounded-md border bg-orange-200 p-2 text-xs text-orange-800 flex align-right">
              {errors.root.login.message}
            </div>
          )}
          {!errors.root?.login && <div className="h-[34px]" />}
          <button
            className="rounded border-2 border-serblue-light bg-white py-2 px-2 text-center disabled:bg-slate-200 disabled:border-blue-200 flex items-center justify-center"
            disabled={!isValid || isSubmitting}
            type="submit"
          >
            {!isSubmitting && 'Submit'}
            {isSubmitting && <Spinner />}
          </button>
        </form>
      )}
    </div>
  )
}

export const Route = createFileRoute('/auth/set-password')({
  component: SetPasswordPage,
})
